import React, { useEffect } from 'react'
import { useShopQntmAdditionalComponent } from '../hooks/use-shop-qntm-additional-component'
import { Typography, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { AdditionalComponentCategoryCard, ShopMainSkeleton } from 'src/Libs/shop'
import useSelectedUserRoutes from 'src/Hooks/useSelectedUserRoutes'
import { Paper } from '@e3dc-react/shell/Elements/Paper'
import QntmWarrantyProductCard from './QntmWarrantyProductCard'
import { usePageActionsContext } from 'src/Libs/header/context'

const useStyles = makeStyles()(theme => ({
  title: {
    fontSize: '32px',
    fontWeight: 400,
  },
  systemConfiguration: {
    marginBottom: '12px',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
    },
  },
  paperContent: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingRight: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    overflowX: 'hidden',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      minWidth: '100%',
      paddingRight: 0,
    },
  },
  gridContainer: {
    display: 'flex',
    overflowX: 'auto',
    width: '100%',
    minWidth: '30px',
    flexWrap: 'nowrap',
    marginLeft: '12px',
  },
}))

/*
 * Additional Component step connecting hook data to the view for Quantum Configurator
 */
const QntmAdditionalComponent: React.FC = () => {
  const { classes } = useStyles()
  const { dispatchPageActions } = usePageActionsContext()
  const { t } = useCustomTranslation()
  const {
    title,
    categories,
    warrantyExtension,
    isWarrantyExtensionSelected,
    isLoading,
    onBack,
    onMoreInfo,
    isProductSelected,
    getSelectedProduct,
    onDecreaseProductQuantity,
    onIncreaseProductQuantity,
    onAddProductToCart,
    onRemoveProductFromCart,
    onAddWarrantyExtensionToCart,
    onRemoveWarrantyExtensionFromCart,
  } = useShopQntmAdditionalComponent()

  useEffect(() => {
    dispatchPageActions?.([
      {
        position: 'left',
        type: 'back',
        onClick: onBack,
      },
    ])

    return () => {
      dispatchPageActions?.([])
    }
  }, [dispatchPageActions, onBack])

  useSelectedUserRoutes(t('shop.system-configure.additional-component-title'))

  if (isLoading) return <ShopMainSkeleton />

  return (
    <Box sx={{ width: '100%', marginRight: 0, marginBottom: '58px' }}>
      <Typography className={classes.title}>{title}</Typography>
      {warrantyExtension && (
        <Paper title={t('shop.system-configuration.quantum-card-title')} className={classes.paperContent}>
          <Box className={classes.gridContainer}>
            {warrantyExtension?.map(warrantyProduct => (
              <QntmWarrantyProductCard
                key={warrantyProduct.id}
                productData={warrantyProduct}
                isProductSelected={isWarrantyExtensionSelected}
                onMoreInfo={onMoreInfo}
                onAddProductToCart={onAddWarrantyExtensionToCart}
                onRemoveProductFromCart={onRemoveWarrantyExtensionFromCart}
              />
            ))}
          </Box>
        </Paper>
      )}
      <Box className={classes.systemConfiguration}>
        {categories.map(category => {
          return (
            <Box key={category.id}>
              <AdditionalComponentCategoryCard
                key={category.id}
                categoryData={category}
                onMoreInfo={onMoreInfo}
                isProductSelected={isProductSelected}
                getSelectedProduct={getSelectedProduct}
                onDecreaseProductQuantity={onDecreaseProductQuantity}
                onIncreaseProductQuantity={onIncreaseProductQuantity}
                onAddProductToCart={onAddProductToCart}
                onRemoveProductFromCart={onRemoveProductFromCart}
              />
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default QntmAdditionalComponent
